import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {LocationCardScratchpadState} from '../../scratchpad/locationCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import locationSetsApi from 'common/src/modules/locationSets/locationSets.api';
import {LocationSet} from 'common/src/modules/locationSets/locationSets.types';

const setLocationSetToolSchema = z.object({
  locationSetId: z.number().describe('The location set ID to set for the location card'),
});

export default function setLocationSetTool() {
  async function execute(
    params: ToolExecuteParams<typeof setLocationSetToolSchema, LocationCardScratchpadState>,
  ) {
    const {locationSetId} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<LocationCardScratchpadState>(
      agentScratchpad,
      NodeType.STORE,
    );

    if (!locationSetId) {
      return toolResult('No location set ID provided');
    }

    if (!validatedScratchpad.card.locationFeedId) {
      return toolResult('No location feed ID set for this card. Please set a location feed first.');
    }

    const availableSets = (await locationSetsApi.getLocationSets(
      validatedScratchpad.card.locationFeedId,
    )) as LocationSet[];

    if (!availableSets.some((s) => s.id === locationSetId)) {
      return toolResult(
        `Location set with ID ${locationSetId} is not available. Available location sets: ${availableSets.map((s) => `${s.id}: ${s.name}`).join(', ')}`,
      );
    }

    validatedScratchpad.card.locationSetId = locationSetId;

    return toolResult(`Location set set to: ${locationSetId}`);
  }

  return tool({
    name: V2CardAgentToolName.SetLocationSet,
    displayName: 'Setting location set',
    description:
      'Set the location set for the location card. When setting a location set, you must also set the feed ID to match the location set feed ID.',
    execute,
    inputSchema: setLocationSetToolSchema,
  });
}
